import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Pencil, TrendingUp, AlertCircle, Trash2, Info, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Items per page in the dropdown
const ITEMS_PER_PAGE = 20;

const SourcingMaster = () => {
  const [sourcing, setSourcing] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editingSourcing, setEditingSourcing] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, sourcingId: null });
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentItemTotal, setCurrentItemTotal] = useState(0);
  const [newSourcing, setNewSourcing] = useState({
    item_id: undefined,
    vendor_id: undefined,
    mode: 'OUTSOURCE',
    sob_share_percent: 100,
    lead_time_days: 3,
    priority: 1
  });
  const [stagingList, setStagingList] = useState([]); // List of entries to submit
  const [loading, setLoading] = useState(false);
  const [tempSelectedItemId, setTempSelectedItemId] = useState(undefined); // Temporary selection before Apply
  
  // State for dropdown open/close
  const [itemDropdownOpen, setItemDropdownOpen] = useState(false);
  
  // Search and pagination state for items dropdown
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemPage, setItemPage] = useState(1);
  
  // Pagination state for sourcing table
  const [sourcingPage, setSourcingPage] = useState(1);
  const [sourcingPageSize, setSourcingPageSize] = useState(10);
  const [sourcingSearchQuery, setSourcingSearchQuery] = useState('');
  
  // Ref for dropdown to handle click outside
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setItemDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (newSourcing.item_id) {
      calculateCurrentTotal(newSourcing.item_id);
    }
  }, [newSourcing.item_id, sourcing]);

  const fetchData = async () => {
    try {
      const [sourcingRes, itemRes, vendorRes] = await Promise.all([
        axios.get(`${API}/item-sourcing`),
        axios.get(`${API}/items`),
        axios.get(`${API}/vendors`)
      ]);
      setSourcing(sourcingRes.data);
      setItems(itemRes.data);
      setVendors(vendorRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const calculateCurrentTotal = (itemId) => {
    const itemSourcing = sourcing.filter(s => s.item_id === itemId);
    const total = itemSourcing.reduce((sum, s) => sum + s.sob_share_percent, 0);
    setCurrentItemTotal(total);
  };

  // Filter and paginate items for the dropdown
  const filteredItems = useMemo(() => {
    let result = items;
    
    // Apply search filter
    if (itemSearchQuery.trim()) {
      const query = itemSearchQuery.toLowerCase().trim();
      result = items.filter(item => 
        item.part_number?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [items, itemSearchQuery]);

  // Paginated items for display
  const paginatedItems = useMemo(() => {
    const startIndex = (itemPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredItems, itemPage]);

  // Total pages
  const totalItemPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  // Reset page when search changes
  useEffect(() => {
    setItemPage(1);
  }, [itemSearchQuery]);

  // Filter and paginate sourcing data for the table
  const filteredSourcing = useMemo(() => {
    if (!sourcingSearchQuery.trim()) return sourcing;
    
    const query = sourcingSearchQuery.toLowerCase().trim();
    return sourcing.filter(s => {
      const item = items.find(i => i.id === s.item_id);
      const vendor = vendors.find(v => v.id === s.vendor_id);
      return (
        item?.part_number?.toLowerCase().includes(query) ||
        item?.description?.toLowerCase().includes(query) ||
        vendor?.name?.toLowerCase().includes(query) ||
        s.mode?.toLowerCase().includes(query)
      );
    });
  }, [sourcing, sourcingSearchQuery, items, vendors]);

  const paginatedSourcing = useMemo(() => {
    const startIndex = (sourcingPage - 1) * sourcingPageSize;
    return filteredSourcing.slice(startIndex, startIndex + sourcingPageSize);
  }, [filteredSourcing, sourcingPage, sourcingPageSize]);

  const totalSourcingPages = Math.ceil(filteredSourcing.length / sourcingPageSize);

  // Reset page when search changes
  useEffect(() => {
    setSourcingPage(1);
  }, [sourcingSearchQuery]);

  // Get allowed modes based on item category
  const getAllowedModes = (itemCategory) => {
    if (!itemCategory) return ['OUTSOURCE'];
    
    if (itemCategory === 'BOP' || itemCategory.startsWith('RM-')) {
      return ['OUTSOURCE']; // BOP and RM can only be purchased
    } else if (itemCategory === 'SFG') {
      return ['INHOUSE', 'OUTSOURCE']; // SFG can be make or buy (no HYBRID label, but can add multiple entries)
    } else if (itemCategory === 'FG') {
      return ['INHOUSE', 'OUTSOURCE'];
    }
    return ['OUTSOURCE'];
  };

  // Get suggested milk run frequency based on SOB%
  const getSuggestedMilkRun = (sobPercent, vendorType) => {
    if (vendorType === 'REMOTE') {
      if (sobPercent >= 50) return 'WEEKLY';
      if (sobPercent >= 30) return 'BIWEEKLY';
      return 'MONTHLY';
    } else { // LOCAL
      if (sobPercent >= 50) return 'DAILY';
      if (sobPercent >= 20) return 'DAILY';
      return 'WEEKLY';
    }
  };

  // Handle item selection - validate and set default mode
  const handleApplyItemSelection = () => {
    if (!tempSelectedItemId) {
      toast.error('Please select an item first');
      return;
    }

    const item = items.find(i => i.id === tempSelectedItemId);
    setSelectedItem(item);
    
    // Calculate current total SOB for this item
    calculateCurrentTotal(tempSelectedItemId);
    
    // Get remaining SOB
    const existingSOB = sourcing
      .filter(s => s.item_id === tempSelectedItemId)
      .reduce((sum, s) => sum + s.sob_share_percent, 0);
    
    const remainingSob = 100 - existingSOB;
    
    const itemCategory = item?.category;
    
    // For BOP and RM, force OUTSOURCE mode
    if (itemCategory === 'BOP' || itemCategory?.startsWith('RM-')) {
      setNewSourcing({ 
        ...newSourcing, 
        item_id: tempSelectedItemId, 
        mode: 'OUTSOURCE',
        vendor_id: undefined,
        sob_share_percent: remainingSob > 0 ? remainingSob : 100
      });
    } else {
      setNewSourcing({ 
        ...newSourcing, 
        item_id: tempSelectedItemId, 
        mode: 'OUTSOURCE',
        vendor_id: undefined,
        sob_share_percent: remainingSob > 0 ? remainingSob : 100
      });
    }

    toast.success(`Item "${item.description}" selected`);
  };

  // Handle mode change - clear vendor for IN_HOUSE
  const handleModeChange = (mode) => {
    if (mode === 'INHOUSE' || mode === 'IN_HOUSE') {
      // IN_HOUSE production should NOT have a vendor
      setNewSourcing({ 
        ...newSourcing, 
        mode, 
        vendor_id: undefined
      });
    } else {
      // For OUTSOURCE or HYBRID, clear vendor (user will select)
      setNewSourcing({ ...newSourcing, mode, vendor_id: undefined });
    }
  };

  const handleAddToStaging = () => {
    if (!newSourcing.item_id) {
      toast.error('Please select an item');
      return;
    }

    // For OUTSOURCE, vendor is required
    if (newSourcing.mode === 'OUTSOURCE' && !newSourcing.vendor_id) {
      toast.error('Please select a vendor for outsource mode');
      return;
    }

    // Check if adding INHOUSE when one already exists in staging or existing rules
    if (newSourcing.mode === 'INHOUSE') {
      const existingInhouse = [...sourcing, ...stagingList].find(
        s => s.item_id === newSourcing.item_id && s.mode === 'INHOUSE'
      );
      if (existingInhouse) {
        toast.error('Only one INHOUSE entry allowed per item');
        return;
      }
    }

    // Check duplicate vendor - each vendor can only be assigned once per item
    if (newSourcing.vendor_id) {
      const duplicateVendor = stagingList.find(
        s => s.item_id === newSourcing.item_id && s.vendor_id === newSourcing.vendor_id
      );
      if (duplicateVendor) {
        toast.error('This vendor is already added. Each vendor can be assigned only once. Please select a different vendor.');
        return;
      }
    }

    // Calculate total of STAGING LIST only (submit will replace existing rules)
    const stagingTotal = stagingList
      .filter(s => s.item_id === newSourcing.item_id)
      .reduce((sum, s) => sum + s.sob_share_percent, 0);
    
    const totalStaging = stagingTotal + newSourcing.sob_share_percent;

    if (totalStaging > 100) {
      toast.error(`Cannot exceed 100%. Staging total: ${stagingTotal.toFixed(1)}%, Adding: ${newSourcing.sob_share_percent}%`);
      return;
    }

    // Add to staging list
    const entryToAdd = {
      ...newSourcing,
      temp_id: Date.now()
    };
    
    setStagingList([...stagingList, entryToAdd]);
    
    const remainingSOB = 100 - totalStaging;
    
    if (totalStaging === 100) {
      toast.success(`Entry added! Total = 100% (Complete). Ready to submit.`);
    } else {
      toast.success(`Entry added! ${remainingSOB.toFixed(1)}% remaining`);
    }
    
    // Reset vendor fields and set remaining SOB%
    const allowedModes = getAllowedModes(selectedItem?.category);
    const defaultMode = allowedModes.includes('INHOUSE') ? 'INHOUSE' : 'OUTSOURCE';
    
    setNewSourcing({ 
      ...newSourcing,
      vendor_id: undefined, 
      sob_share_percent: remainingSOB,
      mode: defaultMode,
      priority: stagingList.filter(s => s.item_id === newSourcing.item_id).length + 2
    });
  };

  const handleRemoveFromStaging = (temp_id) => {
    setStagingList(stagingList.filter(s => s.temp_id !== temp_id));
    toast.info('Entry removed from staging list');
  };

  const handleSubmitAll = async () => {
    if (stagingList.length === 0) {
      toast.error('No entries to submit');
      return;
    }

    // Validate 100% for each item
    const itemGroups = {};
    stagingList.forEach(entry => {
      if (!itemGroups[entry.item_id]) {
        itemGroups[entry.item_id] = 0;
      }
      itemGroups[entry.item_id] += entry.sob_share_percent;
    });

    for (const [itemId, total] of Object.entries(itemGroups)) {
      if (Math.abs(total - 100) > 0.01) {
        const item = items.find(i => i.id === itemId);
        toast.error(`Item "${item?.description || itemId}" must have exactly 100% SOB. Current: ${total.toFixed(1)}%`);
        return;
      }
    }

    setLoading(true);
    try {
      // Delete existing sourcing for each item being replaced
      const itemIds = [...new Set(stagingList.map(e => e.item_id))];
      for (const itemId of itemIds) {
        const existing = sourcing.filter(s => s.item_id === itemId);
        for (const s of existing) {
          await axios.delete(`${API}/item-sourcing/${s.id}`);
        }
      }
      
      // Submit all new entries
      for (const entry of stagingList) {
        const { temp_id, ...dataToSubmit } = entry;
        await axios.post(`${API}/item-sourcing`, dataToSubmit);
      }
      
      toast.success(`✓ Successfully submitted ${stagingList.length} sourcing entries!`);
      setStagingList([]);
      setNewSourcing({ item_id: undefined, vendor_id: undefined, mode: 'OUTSOURCE', sob_share_percent: 100, lead_time_days: 3, priority: 1 });
      setSelectedItem(null);
      setCurrentItemTotal(0);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit sourcing rules: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (s) => {
    setEditingSourcing({
      sob_share_percent: s.sob_share_percent,
      lead_time_days: s.lead_time_days,
      priority: s.priority || 1,
      _id: s.id,
      _item_id: s.item_id,
      _vendor_name: getVendorName(s.vendor_id),
      _item_info: getItemInfo(s.item_id)
    });
    setEditDialog(true);
  };

  const handleUpdate = async (sourcingId) => {
    if (!editingSourcing) return;
    const { sob_share_percent, lead_time_days, priority } = editingSourcing;
    if (sob_share_percent <= 0 || sob_share_percent > 100) {
      toast.error('SOB% must be between 1 and 100');
      return;
    }
    if (lead_time_days < 0) {
      toast.error('Lead time must be 0 or more');
      return;
    }
    if (priority < 1) {
      toast.error('Priority must be 1 or higher');
      return;
    }
    try {
      await axios.put(`${API}/item-sourcing/${sourcingId}`, {
        sob_share_percent, lead_time_days, priority
      });
      toast.success('Sourcing rule updated successfully');
      setEditingSourcing(null);
      setEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update sourcing rule');
    }
  };

  const handleDelete = async (sourcingId) => {
    try {
      await axios.delete(`${API}/item-sourcing/${sourcingId}`);
      toast.success('Sourcing rule deleted successfully');
      setDeleteDialog({ open: false, sourcingId: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sourcing rule');
    }
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getItemCategory = (id) => {
    const item = items.find(i => i.id === id);
    return item?.category || 'N/A';
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || id;
  
  const getVendorType = (id) => vendors.find(v => v.id === id)?.type || 'LOCAL';

  const getModeColor = (mode) => {
    switch (mode) {
      case 'INHOUSE': return 'bg-blue-100 text-blue-800';
      case 'OUTSOURCE': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Group by item to show SOB% totals
  const groupedSourcing = sourcing.reduce((acc, s) => {
    if (!acc[s.item_id]) acc[s.item_id] = [];
    acc[s.item_id].push(s);
    return acc;
  }, {});

  const allowedModes = selectedItem ? getAllowedModes(selectedItem.category) : ['OUTSOURCE'];
  const isBOPorRM = selectedItem && (selectedItem.category === 'BOP' || selectedItem.category.startsWith('RM-'));
  
  // Calculate staging total for current item
  const stagingTotal = selectedItem ? stagingList
    .filter(s => s.item_id === selectedItem.id)
    .reduce((sum, s) => sum + s.sob_share_percent, 0) : 0;
  
  const remainingSOB = 100 - stagingTotal;
  const isSOBComplete = stagingTotal > 0 && Math.abs(100 - stagingTotal) < 0.01;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-medium text-blue-900">SOB% (Share of Business) Distribution - 100% Rule</p>
          <p className="text-sm text-blue-700 mt-1">
            <strong>Critical Rules:</strong><br/>
            • <strong>Total SOB% MUST equal 100%</strong> for each item before production planning<br/>
            • <strong>BOP (Bought-Out Parts):</strong> OUTSOURCE only - distribute 100% across one or more vendors<br/>
            • <strong>RM-Sheet/Tube/Bar:</strong> OUTSOURCE only - distribute 100% across vendors<br/>
            • <strong>SFG (Semi-Finished):</strong> Can be INHOUSE + OUTSOURCE. Example: 60% INHOUSE + 40% Vendor A<br/>
            • <strong>FG (Finished Goods):</strong> Can be INHOUSE + OUTSOURCE<br/>
            <br/>
            <strong>Distribution Examples:</strong><br/>
            • Single vendor: 100% to Vendor A<br/>
            • Multiple vendors: 50% Vendor A + 30% Vendor B + 20% Vendor C = 100%<br/>
            • Hybrid (SFG/FG): 60% INHOUSE + 25% Vendor A + 15% Vendor B = 100%<br/>
            <br/>
            <strong>Milk Run:</strong> Frequency auto-suggested based on SOB% and vendor type (LOCAL/REMOTE)
          </p>
        </div>
      </div>

      <Card data-testid="sourcing-create-card" className="relative z-10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Add Sourcing Rule - SOB% Distribution</CardTitle>
              <CardDescription>
                Step 1: Select Item → Step 2: Add vendor entries until 100%
              </CardDescription>
            </div>
            {selectedItem && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewSourcing({ item_id: undefined, vendor_id: undefined, mode: 'OUTSOURCE', sob_share_percent: 100, lead_time_days: 3, priority: 1 });
                  setSelectedItem(null);
                  setTempSelectedItemId(undefined);
                  setCurrentItemTotal(0);
                }}
              >
                Clear Selection
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Item</label>
              {/* Searchable Dropdown */}
              <div className="relative" ref={dropdownRef}>
                {/* Dropdown trigger button */}
                <button
                  type="button"
                  onClick={() => setItemDropdownOpen(!itemDropdownOpen)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="select-sourcing-item"
                >
                  <span className={tempSelectedItemId ? 'text-foreground' : 'text-muted-foreground'}>
                    {tempSelectedItemId 
                      ? (() => {
                          const item = items.find(i => i.id === tempSelectedItemId);
                          return item ? `[${item.category}] ${item.part_number}` : 'Select item';
                        })()
                      : `Select item (${items.length} items)`
                    }
                  </span>
                  <ChevronRight className={`h-4 w-4 transition-transform ${itemDropdownOpen ? 'rotate-90' : ''}`} />
                </button>
                
                {/* Dropdown content */}
                {itemDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
                    {/* Search input inside dropdown */}
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          type="text"
                          placeholder="Type part number to search..."
                          value={itemSearchQuery}
                          onChange={(e) => setItemSearchQuery(e.target.value)}
                          className="pl-8 h-9"
                          autoFocus
                          data-testid="search-sourcing-item"
                        />
                      </div>
                    </div>
                    
                    {/* Items list */}
                    <div className="max-h-48 overflow-y-auto">
                      {paginatedItems.length === 0 ? (
                        <div className="p-3 text-center text-sm text-slate-500">
                          No items found
                        </div>
                      ) : (
                        paginatedItems.map(item => (
                          <div
                            key={item.id}
                            onClick={() => {
                              setTempSelectedItemId(item.id);
                              setItemDropdownOpen(false);
                              setItemSearchQuery('');
                            }}
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${
                              tempSelectedItemId === item.id ? 'bg-blue-50 text-blue-700' : ''
                            }`}
                          >
                            [{item.category}] {item.part_number} - {item.description?.substring(0, 30)}
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Pagination controls inside dropdown */}
                    {totalItemPages > 1 && (
                      <div className="flex items-center justify-between p-2 border-t bg-slate-50 text-xs">
                        <span className="text-slate-500">
                          Page {itemPage} of {totalItemPages} ({filteredItems.length} items)
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemPage(p => Math.max(1, p - 1));
                            }}
                            disabled={itemPage === 1}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemPage(p => Math.min(totalItemPages, p + 1));
                            }}
                            disabled={itemPage === totalItemPages}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {tempSelectedItemId && (
                <Button
                  onClick={handleApplyItemSelection}
                  className="mt-2 w-full bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  Apply Item Selection
                </Button>
              )}
              {selectedItem && (
                <div className={`mt-2 p-3 rounded text-xs border-2 ${
                  isSOBComplete 
                    ? 'bg-green-50 border-green-300' 
                    : currentItemTotal === 0 
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-orange-50 border-orange-300'
                }`}>
                  <p className="font-semibold text-slate-700">Category: {selectedItem.category}</p>
                  <p className={`font-bold mt-1 text-lg ${isSOBComplete ? 'text-green-700' : 'text-orange-700'}`}>
                    Existing SOB%: {currentItemTotal.toFixed(1)}%
                  </p>
                  {stagingTotal > 0 && (
                    <p className="font-semibold text-blue-700">
                      Staging SOB%: {stagingTotal.toFixed(1)}%
                    </p>
                  )}
                  <p className={`font-semibold ${isSOBComplete ? 'text-green-600' : 'text-blue-700'}`}>
                    Remaining: {remainingSOB.toFixed(1)}%
                  </p>
                  {isSOBComplete ? (
                    <p className="text-green-700 font-bold mt-2 flex items-center gap-1">
                      ✓ 100% Complete - Ready for Planning
                    </p>
                  ) : currentItemTotal === 0 && stagingTotal === 0 ? (
                    <p className="text-blue-700 font-semibold mt-2">
                      → Add first vendor entry
                    </p>
                  ) : stagingTotal > 0 ? (
                    <p className="text-blue-700 font-semibold mt-2">
                      → {stagingList.filter(s => s.item_id === selectedItem.id).length} entries in staging
                    </p>
                  ) : (
                    <p className="text-orange-700 font-semibold mt-2">
                      → Add more entries to reach 100%
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Mode</label>
              <select
                data-testid="select-sourcing-mode"
                value={newSourcing.mode}
                onChange={(e) => handleModeChange(e.target.value)}
                disabled={isBOPorRM}
                className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${isBOPorRM ? 'bg-slate-100' : ''}`}
              >
                {allowedModes.includes('INHOUSE') && <option value="INHOUSE">In-house (METALMAN)</option>}
                {allowedModes.includes('OUTSOURCE') && <option value="OUTSOURCE">Outsource (Vendor)</option>}
              </select>
              {isBOPorRM && (
                <p className="text-xs text-orange-600 mt-1">
                  {selectedItem.category === 'BOP' ? 'BOP must be purchased' : 'Raw materials must be purchased'}
                </p>
              )}
            </div>

            {newSourcing.mode !== 'INHOUSE' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Vendor</label>
                <select
                  data-testid="select-sourcing-vendor"
                  value={newSourcing.vendor_id || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const vendor = vendors.find(v => v.id === value);
                    const suggestedMilkRun = getSuggestedMilkRun(newSourcing.sob_share_percent, vendor?.type);
                    setNewSourcing({ ...newSourcing, vendor_id: value });
                    toast.info(`Suggested milk run for ${vendor?.name}: ${suggestedMilkRun}`);
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select vendor</option>
                  {vendors.filter(v => v.name !== 'METALMAN' && v.code !== 'METALMAN').map(vendor => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name} ({vendor.type})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {newSourcing.mode === 'INHOUSE' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Vendor</label>
                <Input value="METALMAN (Internal)" disabled className="bg-blue-50" />
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">SOB Share %</label>
              <Input
                data-testid="input-sob-percent"
                type="number"
                min="1"
                max={remainingSOB > 0 ? remainingSOB : 100}
                value={newSourcing.sob_share_percent}
                onChange={(e) => setNewSourcing({ ...newSourcing, sob_share_percent: parseFloat(e.target.value) })}
              />
              {selectedItem && remainingSOB > 0 && remainingSOB < 100 && (
                <p className="text-xs text-orange-600 mt-1">
                  Max: {remainingSOB.toFixed(1)}%
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Lead Time (days)</label>
              <Input
                data-testid="input-lead-time"
                type="number"
                min="0"
                value={newSourcing.lead_time_days}
                onChange={(e) => setNewSourcing({ ...newSourcing, lead_time_days: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Priority
                <span className="text-xs text-slate-500 ml-2">(1 = Highest)</span>
              </label>
              <Input
                data-testid="input-priority"
                type="number"
                min="1"
                max="99"
                value={newSourcing.priority}
                onChange={(e) => setNewSourcing({ ...newSourcing, priority: parseInt(e.target.value) || 1 })}
                placeholder="1"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              data-testid="create-sourcing-btn" 
              onClick={handleAddToStaging} 
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={!selectedItem || isSOBComplete}
            >
              <Plus className="w-4 h-4 mr-2" />
              {remainingSOB > 0 && remainingSOB < 100 
                ? `Add to Staging (${remainingSOB.toFixed(1)}% remaining)` 
                : remainingSOB === 100 
                ? 'Add to Staging (100%)' 
                : 'SOB% Complete'}
            </Button>
            
            {selectedItem && remainingSOB > 0 && remainingSOB < 100 && (
              <Button
                onClick={() => {
                  setNewSourcing({ 
                    ...newSourcing, 
                    sob_share_percent: remainingSOB 
                  });
                  toast.info(`SOB% set to ${remainingSOB.toFixed(1)}% (completing to 100%)`);
                }}
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Complete to 100%
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Staging List */}
      {stagingList.length > 0 && (
        <Card className="border-orange-300 bg-orange-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-orange-900">Staging List - Review Before Submitting</CardTitle>
                <CardDescription className="text-orange-700">
                  {stagingList.length} entries ready. Total must = 100% for each item before submission.
                </CardDescription>
              </div>
              <Button
                onClick={handleSubmitAll}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Submitting...' : `Submit All (${stagingList.length})`}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>SOB %</TableHead>
                  <TableHead>Lead Time</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stagingList.map((entry) => (
                  <TableRow key={entry.temp_id} className="bg-white">
                    <TableCell className="font-medium">
                      {getItemInfo(entry.item_id)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={entry.mode === 'INHOUSE' ? 'default' : 'outline'}>
                        {entry.mode}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {entry.mode === 'OUTSOURCE' ? getVendorName(entry.vendor_id) : '-'}
                    </TableCell>
                    <TableCell className="font-bold text-orange-700">
                      {entry.sob_share_percent}%
                    </TableCell>
                    <TableCell>{entry.lead_time_days} days</TableCell>
                    <TableCell>
                    <TableCell className="text-slate-600">{entry.priority || 1}</TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFromStaging(entry.temp_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Summary by Item */}
            <div className="mt-4 p-3 bg-white rounded border border-orange-200">
              <p className="text-sm font-semibold mb-2">Summary by Item:</p>
              {Object.entries(
                stagingList.reduce((acc, entry) => {
                  if (!acc[entry.item_id]) {
                    acc[entry.item_id] = { total: 0, count: 0 };
                  }
                  acc[entry.item_id].total += entry.sob_share_percent;
                  acc[entry.item_id].count += 1;
                  return acc;
                }, {})
              ).map(([itemId, data]) => {
                const item = items.find(i => i.id === itemId);
                const isComplete = Math.abs(data.total - 100) < 0.01;
                return (
                  <div key={itemId} className={`text-sm py-1 ${isComplete ? 'text-green-700' : 'text-red-700'}`}>
                    <span className="font-medium">{item?.description || itemId}</span>: {data.count} entries = {data.total.toFixed(1)}%
                    {isComplete ? ' ✓' : ` (Need ${(100 - data.total).toFixed(1)}% more)`}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="sourcing-list-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Item-Vendor Sourcing Matrix ({filteredSourcing.length} rules)</CardTitle>
              <CardDescription>Complete sourcing configuration with SOB% allocation. Items with 100% SOB go to planning.</CardDescription>
            </div>
            {/* Search input for sourcing table */}
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by part number, vendor..."
                value={sourcingSearchQuery}
                onChange={(e) => setSourcingSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <TrendingUp className="w-4 h-4" />
                      SOB%
                    </div>
                  </TableHead>
                  <TableHead>Lead Time</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Total SOB%</TableHead>
                  <TableHead>Milk Run</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSourcing.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      {sourcingSearchQuery ? 'No matching sourcing rules found' : 'No sourcing rules configured'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedSourcing.map((s) => {
                    const itemSourcing = groupedSourcing[s.item_id] || [];
                    const totalSOB = itemSourcing.reduce((sum, src) => sum + src.sob_share_percent, 0);
                    const isSOBValid = Math.abs(totalSOB - 100) < 0.01;
                    const vendorType = getVendorType(s.vendor_id);
                    const suggestedMilkRun = getSuggestedMilkRun(s.sob_share_percent, vendorType);

                    return (
                      <TableRow key={s.id} data-testid={`sourcing-row-${s.id}`}>
                        <TableCell className="font-medium text-sm">{getItemInfo(s.item_id)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getItemCategory(s.item_id)}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getModeColor(s.mode)}>{s.mode}</Badge>
                        </TableCell>
                        <TableCell>{getVendorName(s.vendor_id)}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-bold">{s.sob_share_percent}%</span>
                        </TableCell>
                        <TableCell>{s.lead_time_days} days</TableCell>
                        <TableCell className="text-slate-600">{s.priority || 1}</TableCell>
                        <TableCell>
                          <Badge className={isSOBValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {totalSOB.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {suggestedMilkRun}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(s)}
                              data-testid={`edit-sourcing-${s.id}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteDialog({ open: true, sourcingId: s.id })}
                              data-testid={`delete-sourcing-${s.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          {filteredSourcing.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Show</span>
                <select
                  value={sourcingPageSize}
                  onChange={(e) => {
                    setSourcingPageSize(Number(e.target.value));
                    setSourcingPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Showing {((sourcingPage - 1) * sourcingPageSize) + 1} to {Math.min(sourcingPage * sourcingPageSize, filteredSourcing.length)} of {filteredSourcing.length} entries
                </span>
                
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSourcingPage(1)}
                    disabled={sourcingPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSourcingPage(p => Math.max(1, p - 1))}
                    disabled={sourcingPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <span className="px-3 py-1 text-sm font-medium">
                    Page {sourcingPage} of {totalSourcingPages || 1}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSourcingPage(p => Math.min(totalSourcingPages, p + 1))}
                    disabled={sourcingPage === totalSourcingPages || totalSourcingPages === 0}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSourcingPage(totalSourcingPages)}
                    disabled={sourcingPage === totalSourcingPages || totalSourcingPages === 0}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, sourcingId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sourcing Rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this sourcing rule. The item's total SOB% will be reduced and may need rebalancing to reach 100%.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.sourcingId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Sourcing Dialog */}
      <Dialog open={editDialog} onOpenChange={(open) => { setEditDialog(open); if (!open) setEditingSourcing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Sourcing Rule</DialogTitle>
            <DialogDescription>
              {editingSourcing?._item_info} — {editingSourcing?._vendor_name}
            </DialogDescription>
          </DialogHeader>
          {editingSourcing && (
            <div className="space-y-4">
              <div>
                <Label>SOB % *</Label>
                <Input
                  data-testid="edit-sob-percent"
                  type="number"
                  min={1}
                  max={100}
                  value={editingSourcing.sob_share_percent}
                  onChange={(e) => setEditingSourcing({ ...editingSourcing, sob_share_percent: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Lead Time (days) *</Label>
                <Input
                  data-testid="edit-lead-time"
                  type="number"
                  min={0}
                  value={editingSourcing.lead_time_days}
                  onChange={(e) => setEditingSourcing({ ...editingSourcing, lead_time_days: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Priority *</Label>
                <span className="text-xs text-slate-500 ml-2">(Lower = Higher priority)</span>
                <Input
                  data-testid="edit-priority"
                  type="number"
                  min={1}
                  value={editingSourcing.priority}
                  onChange={(e) => setEditingSourcing({ ...editingSourcing, priority: parseInt(e.target.value) || 1 })}
                />
              </div>
              <p className="text-xs text-slate-500">Changes will apply to future MRO generation and shipment creation. Existing MROs and shipments are not affected.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialog(false); setEditingSourcing(null); }}>Cancel</Button>
            <Button data-testid="edit-sourcing-save-btn" onClick={() => handleUpdate(editingSourcing?._id)}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SourcingMaster;
